// Users.jsx
import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { hasPerm } from '../utils/perm';

const ALL_COLUMNS = ['name','email','roles','mfa','created_at','actions'];

function RoleChips({ roles=[] }) {
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
      {roles.map(r => (
        <span
          key={r}
          style={{
            fontSize:12,
            padding:'2px 8px',
            border:'1px solid var(--hairline, #e5e7eb)',
            borderRadius:999,
            background:'var(--code-bg, #f1f5f9)',
            color:'var(--fg, #0b1220)'
          }}
        >
          {r}
        </span>
      ))}
    </div>
  );
}

function Users() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [roles, setRoles] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showRoles, setShowRoles] = useState(null); // user object
  const [pwdResetId, setPwdResetId] = useState(null);
  const [infoOpen, setInfoOpen] = useState(null);
  const [mfaBusyId, setMfaBusyId] = useState(null);

  const canCreate = hasPerm('users:create');
  const canUpdate = hasPerm('users:update');

  async function load() {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([
        api.get('/users', { params: q ? { q } : undefined }),
        api.get('/roles')
      ]);
      setItems(u.data || []);
      setRoles(r.data || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [q]);

  async function addUser(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const name = fd.get('name')?.trim();
    const email = fd.get('email')?.trim();
    const password = fd.get('password')?.trim();
    const roleSel = String(fd.get('role') || '').trim();
    if (!name || !email || !password) return alert('All fields are required');
    if (!roleSel) return alert('Role is required');
    await api.post('/users', { name, email, password, roles: [roleSel] });
    setShowAdd(false);
    await load();
  }

  async function saveRoles(userId, rolesSel) {
    const firstRole = Array.isArray(rolesSel) ? rolesSel[0] : '';
    if (!firstRole) return alert('Role is required');
    await api.patch(`/users/${userId}/roles`, { roles: [firstRole] });
    setShowRoles(null);
    await load();
  }

  async function resetPassword(userId, password) {
    await api.post(`/users/${userId}/reset-password`, { password });
    setPwdResetId(null);
    alert('Password updated');
  }

  async function updateMfa(user, { enabled, reset = false }) {
    try {
      setMfaBusyId(user.id);
      await api.patch(`/users/${user.id}/mfa`, { enabled, reset });
      await load();
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Failed to update MFA';
      alert(msg);
    } finally {
      setMfaBusyId(null);
    }
  }

  const filtered = useMemo(() => items, [items]);

  return (
    <div className="page" style={{ color:'var(--fg,#0b1220)' }}>
      <div className="row between center header-row" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <h2 style={{ margin: 0 }}>Users</h2>
          <button
            type="button"
            className="btn btn-sm secondary"
            onClick={() => setInfoOpen('users')}
            style={{ padding: '2px 6px', fontSize: '12px', lineHeight: '1' }}
            title="Learn more about Users"
          >
            ℹ️
          </button>
        </div>
        <div className="actions-row">
          <input
            className="input"
            placeholder="Search name/email"
            value={q}
            onChange={e=>setQ(e.target.value)}
            style={{ minWidth:220 }}
          />
          {canCreate && <button className="btn add-btn" onClick={()=>setShowAdd(true)}>Add User</button>}
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table" style={{ overflowX:'auto' }}>
          <table style={{ width:'100%' }}>
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Roles</th><th>MFA</th><th>Status</th><th>Created</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id}>
                  <td data-label="Name">{u.name}</td>
                  <td data-label="Email">{u.email}</td>
                  <td data-label="Roles"><RoleChips roles={u.roles} /></td>
                  <td data-label="MFA">
                    <span style={{ color: u.mfa_enabled ? 'var(--success,#059669)' : 'var(--muted,#475569)' }}>
                      {u.mfa_enabled ? 'Enabled' : (u.mfa_configured ? 'Ready' : 'Not set')}
                    </span>
                  </td>
                  <td data-label="Status">
                    <span style={{ color: u.is_active ? 'var(--success,#059669)' : 'var(--danger,#b91c1c)' }}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td data-label="Created" className="mono" style={{ color:'var(--muted,#475569)' }}>
                    {new Date(u.created_at).toLocaleString()}
                  </td>
                  <td data-label="Actions" className="actions-cell" style={{ textAlign:'right' }}>
                    {canUpdate && (
                      <div className="actions-wrap">
                        <button className="btn small" onClick={()=>setShowRoles({ ...u, roles: [u.roles?.[0] || 'Viewer'] })}>Edit Role</button>
                        <button className="btn small outline" onClick={()=>setPwdResetId(u.id)}>Reset Password</button>
                        {u.mfa_enabled && (
                          <button
                            className="btn small outline"
                            disabled={mfaBusyId === u.id}
                            onClick={() => {
                              if (!window.confirm('Disable MFA for this user?')) return;
                              updateMfa(u, { enabled: false });
                            }}
                          >
                            Disable MFA
                          </button>
                        )}
                        {!u.mfa_enabled && u.mfa_configured && (
                          <button
                            className="btn small outline"
                            disabled={mfaBusyId === u.id}
                            onClick={() => updateMfa(u, { enabled: true })}
                          >
                            Enable MFA
                          </button>
                        )}
                        {u.mfa_configured && (
                          <button
                            className="btn small danger"
                            disabled={mfaBusyId === u.id}
                            onClick={() => {
                              if (!window.confirm('Reset MFA for this user? They will need to set it up again.')) return;
                              updateMfa(u, { reset: true });
                            }}
                          >
                            Reset MFA
                          </button>
                        )}
                        {hasPerm('users:deactivate') && (
                          u.is_active ? (
                            <button
                              className="btn small danger"
                              onClick={async()=>{ await api.post(`/users/${u.id}/activate`, { active:false }); await load(); }}
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button
                              className="btn small"
                              onClick={async()=>{ await api.post(`/users/${u.id}/activate`, { active:true }); await load(); }}
                            >
                              Activate
                            </button>
                          )
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {loading && <div style={{ padding:12 }}>Loading...</div>}
        {!loading && filtered.length===0 && <div style={{ padding:12, color:'var(--muted,#475569)' }}>No users</div>}
      </div>

      {/* Add User Modal */}
      {showAdd && (
        <div className="modal">
          <div className="modal-card">
            <h3>Add User</h3>
            <form onSubmit={addUser}>
              <div className="row">
                <div className="col"><label>Name</label><input className="input" name="name" required /></div>
                <div className="col"><label>Email</label><input className="input" name="email" type="email" required /></div>
              </div>
              <div className="row">
                <div className="col"><label>Password</label><input className="input" name="password" type="password" required minLength={8} /></div>
              </div>
              <div style={{ marginTop: 8, fontWeight:600 }}>Role</div>
              <div style={{ marginTop:8 }}>
                <select className="input" name="role" defaultValue={roles.some(r => r.name === 'Viewer') ? 'Viewer' : (roles[0]?.name || '')} required>
                  {roles.map(r => (
                    <option key={r.name} value={r.name}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div className="row end" style={{ marginTop: 16, gap:8 }}>
                <button type="button" className="btn outline" onClick={()=>setShowAdd(false)}>Cancel</button>
                <button type="submit" className="btn">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Roles Modal */}
      {showRoles && (
        <div className="modal">
          <div className="modal-card">
            <h3>Edit Role — {showRoles.name}</h3>
            <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginTop:8 }}>
              {roles.map(r => (
                <label key={r.name} style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <input
                    type="radio"
                    name="edit-role"
                    value={r.name}
                    checked={showRoles.roles?.[0] === r.name}
                    onChange={e => {
                      const val = e.target.value;
                      setShowRoles({ ...showRoles, roles: [val] });
                    }}
                  />
                  <span>{r.name}</span>
                </label>
              ))}
            </div>
            <div className="row end" style={{ marginTop: 16, gap:8 }}>
              <button className="btn outline" onClick={()=>setShowRoles(null)}>Cancel</button>
              <button className="btn" onClick={()=>saveRoles(showRoles.id, showRoles.roles || [])}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {pwdResetId && (
        <div className="modal">
          <div className="modal-card">
            <h3>Reset Password</h3>
            <PasswordForm onCancel={()=>setPwdResetId(null)} onSave={(pwd)=>resetPassword(pwdResetId, pwd)} />
          </div>
        </div>
      )}

      {/* Info Modal */}
      {infoOpen === 'users' && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
          onClick={() => setInfoOpen(null)}
        >
          <div
            className="card"
            style={{
              maxWidth: 500, maxHeight: '80vh', overflowY: 'auto', padding: 24,
              borderRadius: 8, background: 'var(--card,#fff)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 600 }}>Users Management</div>
              <button
                className="btn outline small"
                onClick={() => setInfoOpen(null)}
                style={{ padding: '2px 6px', fontSize: '12px' }}
              >
                ✕
              </button>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--fg,#0b1220)' }}>
              <p><strong>Users</strong> are team members who can access your system. Each user has specific roles that determine what they can do.</p>
              <p><strong>User Management Features:</strong></p>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li><strong>Add User:</strong> Create new team member accounts with name, email, and password</li>
                <li><strong>Edit Roles:</strong> Assign or update user roles to control their permissions</li>
                <li><strong>Reset Password:</strong> Help users regain access if they forget their password</li>
                <li><strong>Activate/Deactivate:</strong> Control user access without deleting their account</li>
              </ul>
              <p><strong>User Information Tracked:</strong></p>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li>Name and email address</li>
                <li>Assigned roles and permissions</li>
                <li>Account status (Active/Inactive)</li>
                <li>Creation date</li>
              </ul>
              <p><strong>Best Practices:</strong></p>
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li>Assign only necessary roles to minimize security risk</li>
                <li>Deactivate accounts when users leave instead of deleting</li>
                <li>Regularly review active users and their permissions</li>
                <li>Use strong passwords for administrative accounts</li>
              </ul>
              <p><strong>Roles Overview:</strong></p>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li><strong>Owner:</strong> Full system access and user management</li>
                <li><strong>Manager:</strong> Can view reports and manage operations</li>
                <li><strong>Cashier:</strong> Can process sales and returns</li>
                <li><strong>Viewer:</strong> Read-only access to view reports and data</li>
                <li><strong>Auditor:</strong> Can view and audit Invoices and Credit Notes</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .page { padding: 16px; background: var(--bg,#f8fafc); }
        .row { display:flex; gap:12px; }
        .between { justify-content: space-between; }
        .center { align-items: center; }
        .end { justify-content: flex-end; }
        .col { flex:1; display:flex; flex-direction:column; gap:6px; }
        .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace; }

        /* Cards / tables */
        .card { background: var(--card,#fff); color: var(--fg,#0b1220);
                border:1px solid var(--hairline,#e5e7eb); border-radius:12px; padding:12px; }
        .table th, .table td { padding: 10px; border-bottom:1px solid var(--hairline,#e5e7eb); text-align:left; }
        .table thead th { color: var(--muted,#475569); font-weight:600; }
        .table table { width:100%; min-width:760px; border-collapse:collapse; }
        .actions-wrap { display:flex; gap:8px; justify-content:flex-end; flex-wrap:wrap; }
        .actions-row { display:flex; gap:8px; align-items:center; }

        /* Inputs (shared) */
        .input {
          width: 100%;
          padding: 8px 10px;
          border: 1px solid var(--hairline,#e5e7eb);
          border-radius: 8px;
          background: var(--card,#fff);
          color: var(--fg,#0b1220);
          outline: none;
        }
        .input:focus {
          border-color: var(--accent,#7c3aed);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent,#7c3aed) 25%, transparent);
        }

        /* Buttons */
        .btn {
          padding:8px 12px; border-radius:8px; border:1px solid var(--hairline,#e5e7eb);
          background: var(--link,#2563eb); color:#fff; cursor:pointer;
        }
        .btn.small { padding:6px 8px; font-size:12px; }
        .btn.outline { background:transparent; color:var(--fg,#0b1220); }
        .btn.outline:hover { background: color-mix(in srgb, var(--fg,#0b1220) 8%, transparent); }
        .btn.danger { background: var(--danger,#b91c1c); border-color: color-mix(in srgb, var(--danger,#b91c1c) 70%, transparent); }
        .btn:disabled { opacity:0.6; cursor:not-allowed; }

        /* Modals */
        .modal { position:fixed; inset:0; background: rgba(0,0,0,0.5); display:flex; align-items:center; justify-content:center; z-index:1000; }
        .modal-card { width:min(680px,90vw); background:var(--card,#fff); color:var(--fg,#0b1220);
                      border-radius:12px; border:1px solid var(--hairline,#e5e7eb); padding:16px; }

        /* Responsive table collapse */
        @media (max-width: 768px) {
          .header-row { flex-direction:column; align-items:flex-start; gap:10px; }
          .actions-row { width:100%; flex-wrap:wrap; }
          .actions-row .input { flex:1 1 180px; min-width:0; }
          .add-btn { flex:0 0 auto; }
          .table { overflow-x: visible; }
          .table table { min-width: 0; }
          thead { display:none; }
          tbody tr { display:block; border-bottom:1px solid var(--hairline,#e5e7eb); padding:10px; }
          tbody td { display:flex; justify-content:space-between; align-items:flex-start; gap:6px; padding:6px 2px; border:none; word-break:break-word; }
          tbody td::before { content: attr(data-label); font-weight:600; margin-right:8px; color:var(--muted,#475569); white-space:nowrap; }
          .actions-cell { text-align:left; width:100%; align-items:flex-start; }
          .actions-wrap {
            width:100%;
            display:flex;
            flex-direction:column;
            gap:8px;
          }
          .actions-wrap .btn { width:100%; justify-content:center; }
        }
      `}</style>
    </div>
  );
}

function PasswordForm({ onCancel, onSave }) {
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  return (
    <form onSubmit={(e)=>{ e.preventDefault(); if(pwd!==pwd2) return alert('Passwords do not match'); if(pwd.length<8) return alert('Min length 8'); onSave(pwd); }}>
      <div className="row">
        <div className="col"><label>New Password</label><input className="input" type="password" value={pwd} onChange={e=>setPwd(e.target.value)} minLength={8} required /></div>
        <div className="col"><label>Confirm Password</label><input className="input" type="password" value={pwd2} onChange={e=>setPwd2(e.target.value)} minLength={8} required /></div>
      </div>
      <div className="row end" style={{ marginTop: 16, gap:8 }}>
        <button type="button" className="btn outline" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn">Update</button>
      </div>
    </form>
  );
}

export default Users;


